import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { endSession } from '../auth/session';

// ── Config ────────────────────────────────────────────────────────────────────
const INACTIVITY_MS      = 13 * 60 * 1000; // Show warning after 13 min inactivity
const COUNTDOWN_SECONDS  = 60;             // 60s to respond before auto-logout

// Events that count as "activity"
const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];

/**
 * Monitors user inactivity across all protected pages.
 *
 * Timeline:
 *   0 ─────────────── 13 min ─── warning modal (60s countdown) ─── 15 min → logout
 *
 * Any user activity resets the 13-minute clock.
 * "Continue Session" dismisses the modal and resets the clock.
 * "Logout" or countdown reaching 0 clears the session and redirects to /login.
 */
function SessionTimeout() {
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown]     = useState(COUNTDOWN_SECONDS);

  // Use refs for timers — changing them must not trigger re-renders
  const inactivityTimer   = useRef(null);
  const countdownInterval = useRef(null);

  // Track when activity last happened
  const lastActivityTime = useRef(Date.now());

  // ── Logout ─────────────────────────────────────────────────────────────────
  const logout = useCallback((reason) => {
    clearTimeout(inactivityTimer.current);
    clearInterval(countdownInterval.current);
    endSession();
    navigate('/session-expired', { state: { reason: reason || 'Your session has expired due to inactivity.' } });
  }, [navigate]);

  // ── Start countdown (called when inactivity timer fires) ───────────────────
  const startCountdown = useCallback(() => {
    setShowWarning(true);
    setCountdown(COUNTDOWN_SECONDS);

    let remaining = COUNTDOWN_SECONDS;
    countdownInterval.current = setInterval(() => {
      remaining -= 1;
      setCountdown(remaining);
      if (remaining <= 0) {
        clearInterval(countdownInterval.current);
        logout();
      }
    }, 1000);
  }, [logout]);

  // Track whether the warning is visible (ref avoids stale closure issues)
  const warningVisible = useRef(false);

  // ── Reset the inactivity clock (called on any user activity) ───────────────
  const resetTimer = useCallback(() => {
    // Ignore activity while the warning modal is showing —
    // user must click "Continue Session" or "Logout"
    if (warningVisible.current) return;

    lastActivityTime.current = Date.now();
    clearTimeout(inactivityTimer.current);
    clearInterval(countdownInterval.current);
    setShowWarning(false);
    setCountdown(COUNTDOWN_SECONDS);

    // Schedule the next warning
    inactivityTimer.current = setTimeout(startCountdown, INACTIVITY_MS);
  }, [startCountdown]);

  // ── Handle screen wake / tab return ──────────────────────────────────────
  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState !== 'visible') return;

    const elapsed = Date.now() - lastActivityTime.current;
    const totalTimeout = INACTIVITY_MS + (COUNTDOWN_SECONDS * 1000);

    if (elapsed >= totalTimeout) {
      // Session should have expired while screen was off
      logout('Your session has expired due to inactivity while you were away.');
    } else if (elapsed >= INACTIVITY_MS) {
      // Past warning threshold — show warning with reduced countdown
      const remaining = Math.ceil((totalTimeout - elapsed) / 1000);
      if (remaining > 0) {
        clearTimeout(inactivityTimer.current);
        clearInterval(countdownInterval.current);
        setShowWarning(true);
        warningVisible.current = true;
        setCountdown(remaining);

        let left = remaining;
        countdownInterval.current = setInterval(() => {
          left -= 1;
          setCountdown(left);
          if (left <= 0) {
            clearInterval(countdownInterval.current);
            logout('Your session has expired due to inactivity.');
          }
        }, 1000);
      } else {
        logout('Your session has expired due to inactivity while you were away.');
      }
    }
  }, [logout, startCountdown]);

  // ── Dismiss modal and restart the inactivity clock ─────────────────────────
  const continueSession = useCallback(() => {
    warningVisible.current = false;
    clearTimeout(inactivityTimer.current);
    clearInterval(countdownInterval.current);
    setShowWarning(false);
    setCountdown(COUNTDOWN_SECONDS);
    inactivityTimer.current = setTimeout(startCountdown, INACTIVITY_MS);
  }, [startCountdown]);

  // Keep warningVisible ref in sync with state
  useEffect(() => {
    warningVisible.current = showWarning;
  }, [showWarning]);

  // ── Wire up activity listeners on mount ────────────────────────────────────
  useEffect(() => {
    ACTIVITY_EVENTS.forEach((e) =>
      window.addEventListener(e, resetTimer, { passive: true })
    );

    // Listen for 401 responses dispatched by the Axios interceptor.
    // Wrapped in an arrow function deliberately: passing `logout` directly makes
    // the browser call it with the DOM Event as its `reason` argument. Because an
    // Event is truthy it bypassed the default message and was passed as navigate
    // state, which the browser cannot clone into history — producing
    // "DataCloneError: Event object could not be cloned" and leaving the user
    // logged out but stranded on the page instead of redirected.
    const handleSessionExpired = () => logout();
    window.addEventListener('session-expired', handleSessionExpired);

    // Detect when user returns to the tab / wakes screen
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Start the first timer
    resetTimer();

    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, resetTimer));
      window.removeEventListener('session-expired', handleSessionExpired);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearTimeout(inactivityTimer.current);
      clearInterval(countdownInterval.current);
    };
  }, [resetTimer, logout, handleVisibilityChange]);

  // Nothing to render when session is active
  if (!showWarning) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-box">
        <h2>Session Expiring</h2>
        <p>
          Your session will expire in{' '}
          <strong>{countdown} second{countdown !== 1 ? 's' : ''}</strong> due
          to inactivity.
        </p>
        <div className="modal-actions">
          <button className="btn btn-primary" onClick={continueSession}>
            Continue Session
          </button>
          <button className="btn btn-secondary" onClick={logout}>
            Logout
          </button>
        </div>
      </div>
    </div>
  );
}

export default SessionTimeout;
